/**
 * Projectile.ts
 * Star Shard projectile entity for Starfall Courier.
 *
 * Star Shards are Nova's ranged attack: fired in a 3-shot burst on Attack press,
 * 0.5s cooldown between bursts. Each shard travels horizontally in the direction
 * Nova is facing, despawning on platform collision, TTL expiry (2s), or exiting
 * level bounds (with one viewport-width buffer).
 *
 * Pool contract: Projectile implements `reset()` so ObjectPool can recycle
 * instances. Fire with `spawn()` instead of the constructor.
 *
 * Rules (AGENTS.md):
 *   - No React imports.
 *   - No async calls.
 *   - No Skia imports.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD.
 * See ROADMAP.md Phase 2.
 *
 * @module Projectile
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import {
  VIEWPORT_WIDTH,
  VIEWPORT_HEIGHT,
} from '../../constants/gameConstants';

/** Projectile hitbox dimensions (logical pixels). */
export const PROJECTILE_WIDTH = 8;
export const PROJECTILE_HEIGHT = 8;

/** Travel speed in logical pixels per second. */
export const PROJECTILE_SPEED = 400;

/**
 * Gem Shard travel speed multiplier. Gem Shards are the W3+ weapon-pickup
 * variant per ROADMAP Phase 4 — faster than Star Shards, finite ammo. The
 * speed boost is expressed as a multiplier so re-tuning `PROJECTILE_SPEED`
 * keeps the variants in proportion.
 */
export const GEM_SHARD_SPEED_MULT = 1.5;

/** Fire Orb speed multiplier — slower, larger projectile with deferred AoE. */
export const FIRE_ORB_SPEED_MULT = 0.6;

/** Fire Orb hitbox dimensions in logical pixels — 2× default for AoE feel. */
export const FIRE_ORB_WIDTH = 16;
export const FIRE_ORB_HEIGHT = 16;

/** Boomerang travel time (s) — round-trip arc back to origin. */
export const BOOMERANG_TTL = 3.0;

/** Boomerang hitbox dimensions in logical pixels — slightly larger than default. */
export const BOOMERANG_WIDTH = 10;
export const BOOMERANG_HEIGHT = 10;

/** Freeze duration applied to a struck enemy when an Ice Crystal connects. */
export const ICE_CRYSTAL_FREEZE_DURATION = 2.0;

/** Discriminator for the projectile variant table. */
export type ProjectileKind = 'star_shard' | 'gem_shard' | 'boomerang' | 'fire_orb' | 'ice_crystal';

interface ProjectileConfig {
  readonly speedMult: number;
  readonly width: number;
  readonly height: number;
  readonly ttl: number;
  readonly freezeDuration: number;
  /** 'linear' = constant velocity; 'arc' = linearly-reversing velocity over TTL. */
  readonly behavior: 'linear' | 'arc';
}

/**
 * Per-variant config table — single source of truth for the projectile
 * variant table. Add new ProjectileKind values here.
 *
 * DECISION: lookup table over per-class subclasses — keeps the pool
 * contract trivial (one Projectile class, one pool) and behaviour
 * deltas are data-driven.
 */
const VARIANT_CONFIG: Record<ProjectileKind, ProjectileConfig> = {
  star_shard: { speedMult: 1.0, width: PROJECTILE_WIDTH, height: PROJECTILE_HEIGHT, ttl: 2.0, freezeDuration: 0, behavior: 'linear' },
  gem_shard: { speedMult: GEM_SHARD_SPEED_MULT, width: PROJECTILE_WIDTH, height: PROJECTILE_HEIGHT, ttl: 2.0, freezeDuration: 0, behavior: 'linear' },
  boomerang: { speedMult: 1.0, width: BOOMERANG_WIDTH, height: BOOMERANG_HEIGHT, ttl: BOOMERANG_TTL, freezeDuration: 0, behavior: 'arc' },
  fire_orb: { speedMult: FIRE_ORB_SPEED_MULT, width: FIRE_ORB_WIDTH, height: FIRE_ORB_HEIGHT, ttl: 2.0, freezeDuration: 0, behavior: 'linear' },
  ice_crystal: { speedMult: 1.0, width: PROJECTILE_WIDTH, height: PROJECTILE_HEIGHT, ttl: 2.0, freezeDuration: ICE_CRYSTAL_FREEZE_DURATION, behavior: 'linear' },
};

/**
 * Star Shard — Nova's pooled ranged projectile.
 *
 * Lifecycle:
 *   1. Acquired from pool via `pool.acquire()`.
 *   2. Initialised with `spawn(x, y, facingRight)`.
 *   3. Updated each tick via `update(dt, level)`.
 *   4. When `dead === true`, returned to pool via `pool.release(shard)`.
 */
export class Projectile extends BaseEntity {
  /** Monotonic ID counter for stable React keys across pool recycling. */
  private static nextId = 1;

  /**
   * Unique spawn identifier — reassigned each time the shard is recycled from
   * the pool. Used for stable React list keys in the renderer.
   * DECISION: Separate from BaseEntity.id (which is readonly and assigned once
   * at construction) because pooled projectiles are reused across spawns and
   * need a fresh key each time to avoid React key collisions.
   */
  public spawnId = 0;

  /** True while this shard is actively travelling (between spawn and despawn). */
  public active = false;

  /**
   * Variant of this projectile — cosmetic + speed differences. Reset to
   * `'star_shard'` on pool release so a recycled instance never inherits
   * the previous fire's kind.
   */
  public kind: ProjectileKind = 'star_shard';

  /**
   * Freeze duration (seconds) applied to a struck enemy when this shard
   * connects. Set per-kind on spawn — non-zero only for `'ice_crystal'`.
   * Read by the level enemy-hit code when wired up.
   */
  public freezeDuration = 0;

  /** Seconds since spawn — used for TTL despawn. */
  private lifeTime = 0;

  /**
   * Initial vx captured at spawn time. Used by the `'arc'` behaviour to
   * compute the return trajectory: `vx(t) = _initialVx × (1 − 2t/TTL)`,
   * which integrates to a parabolic position curve that returns to the
   * spawn point at `t = TTL`.
   */
  private _initialVx = 0;

  constructor() {
    super(0, 0, PROJECTILE_WIDTH, PROJECTILE_HEIGHT);
    this.dead = true; // starts dead until spawned
  }

  /**
   * Initialise the shard at the given world position, travelling in the
   * direction Nova is facing.
   *
   * @param x           - Spawn X in world space (logical px).
   * @param y           - Spawn Y in world space (logical px).
   * @param facingRight - true = travels right; false = travels left.
   * @param kind        - Projectile variant. Defaults to `'star_shard'`
   *                      so callers that don't care about Gem Shards keep
   *                      working unchanged.
   */
  public spawn(x: number, y: number, facingRight: boolean, kind: ProjectileKind = 'star_shard'): void {
    const config = VARIANT_CONFIG[kind];
    this.spawnId = Projectile.nextId++;
    this.kind = kind;
    this.x = x;
    this.y = y;
    this.width = config.width;
    this.height = config.height;
    const speed = PROJECTILE_SPEED * config.speedMult;
    this._initialVx = facingRight ? speed : -speed;
    this.vx = this._initialVx;
    this.vy = 0;
    this.dead = false;
    this.active = true;
    this.lifeTime = 0;
    this.freezeDuration = config.freezeDuration;
  }

  /**
   * Reset state for pool recycling. Called by ObjectPool.release().
   * Restores default hitbox dimensions and clears variant-specific state
   * so a recycled instance never inherits a previous fire's payload.
   */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.dead = true;
    this.active = false;
    this.lifeTime = 0;
    this.kind = 'star_shard';
    this.width = PROJECTILE_WIDTH;
    this.height = PROJECTILE_HEIGHT;
    this.freezeDuration = 0;
    this._initialVx = 0;
  }

  /**
   * Advance the shard by one fixed timestep.
   * Despawns if it exceeds TTL, hits a platform, or leaves the level bounds
   * (extended by one viewport width/height to allow hits near edges).
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Current level geometry for platform collision.
   */
  public update(dt: number, level: LevelRuntime): void {
    if (!this.active) return;

    this.lifeTime += dt;
    const config = VARIANT_CONFIG[this.kind];
    // DECISION: despawn after per-variant TTL to prevent pool exhaustion in
    // long levels and to anchor the boomerang's round-trip duration.
    if (this.lifeTime >= config.ttl) {
      this.dead = true;
      this.active = false;
      return;
    }

    if (config.behavior === 'arc') {
      // DECISION: linearly reverse vx across TTL — integrates to a
      // parabolic path that lands the boomerang back near its spawn x
      // at lifeTime = TTL.
      this.vx = this._initialVx * (1 - 2 * this.lifeTime / config.ttl);
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // Despawn when travelling beyond one full viewport width past the level bounds
    // so shards fired near the edge still have time to register hits.
    // DECISION: use right/bottom edge for left/top bounds so projectile is fully off-screen.
    const rightEdge = this.x + this.width;
    const bottomEdge = this.y + this.height;
    if (rightEdge < -VIEWPORT_WIDTH || this.x > level.width + VIEWPORT_WIDTH) {
      this.dead = true;
      this.active = false;
      return;
    }

    // Vertical out-of-bounds (above/below level)
    if (bottomEdge < -VIEWPORT_HEIGHT || this.y > level.height + VIEWPORT_HEIGHT) {
      this.dead = true;
      this.active = false;
      return;
    }

    // Platform collision — despawn on contact.
    // DECISION: inline AABB check to avoid allocating a hitbox object every update.
    // With 50 projectiles at 60fps, getHitbox() would create avoidable GC churn.
    const px = this.x;
    const py = this.y;
    const pw = this.width;
    const ph = this.height;
    for (const plat of level.platforms) {
      if (
        px < plat.x + plat.width &&
        px + pw > plat.x &&
        py < plat.y + plat.height &&
        py + ph > plat.y
      ) {
        this.dead = true;
        this.active = false;
        return;
      }
    }
  }
}
